import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import React, { useEffect, useState } from 'react';
import { setIsTldAlertModalOpen, setJmrResults } from 'src/store/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import Checkbox from '@amzn/meridian/checkbox';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import starTokens from '@amzn/meridian-tokens/base/icon/star';
import { isJobAtCapacity, JmrJobTypes, TableDataRow } from 'src/utils/jmrUtils';
import Input from '@amzn/meridian/input';
import { calculatePlacementHours, toggleJob } from 'src/utils/placementSelectionUtils';
import ConflictTag from './conflictPopover';
import TldAlertModal from '../../tldAlertModal/tldAlertModal';
import Tooltip from '@amzn/meridian/tooltip';

const JobMatchesTable = () => {
  const { jmrFilter, jmrResults, selectedTasks, backgroundFormData, tldAlertModal } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const dispatch = useAppDispatch();
  const [tableData, setTableData] = useState(jmrResults);
  const [isTldModalOpen, setIsTldModalOpen] = useState(false);

  useEffect(() => {
    // filter, sorting, and pagination
    const filteredData = jmrResults.filter((row) => row.jobType === jmrFilter.jobType);
    setTableData(filteredData);
  }, [jmrResults, jmrFilter.jobType]);

  const setTotalHours = (id: string, totalHours: string) => {
    dispatch(
      setJmrResults(
        jmrResults.map((jmrResult) => {
          if (jmrResult.id === id) {
            const newJmrResult = { ...jmrResult, totalHours };
            return {
              ...newJmrResult,
              allocatedTasks: selectedTasks.map((task) => ({
                taskId: task.taskId,
                taskName: task.label,
                assignedValue: calculatePlacementHours(newJmrResult, task),
              })),
            };
          } else {
            return jmrResult;
          }
        }),
      ),
    );
  };

  const getTotalHoursErrorMsg = (row: TableDataRow) => {
    const totalSelectedHours = jmrResults
      .filter((r) => r.isChecked)
      .reduce((acc, row) => acc + (+row?.totalHours || 0), 0);

    const actualShiftLength = backgroundFormData.cannotWorkNormalShiftHrs
      ? +backgroundFormData.limitedHoursPerDay
      : backgroundFormData.shiftLength;

    if (isNaN(+row.totalHours) || +row.totalHours <= 0) {
      return 'Must be a number greater than zero.';
    } else if (totalSelectedHours > actualShiftLength) {
      return 'Allocated hours exceed shift length';
    } else if (!canWorkFullShift(row) && +row.totalHours > row.maximumHoursAssociateCanDo) {
      return 'Allocated hours exceed restrictions';
    }

    return null;
  };

  const canWorkFullShift = ({ conflicts }: TableDataRow) => {
    // either there are no conflicts, or the job has been modified
    return !conflicts?.length || conflicts.every((c) => c.appliedModifications?.length);
  };

  const onChecked = (resultId: string) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId,
        }),
      );
      setIsTldModalOpen(true);
    } else {
      toggleJob(resultId, jmrResults, dispatch);
    }
  };

  const isDisabled = (row: TableDataRow) => row.disable || isJobAtCapacity(row);

  return (
    <Table headerRows={1} rowComponents={[UncontrolledExpandableRow, TableRow]}>
      <TableRow>
        <TableCell sortColumn="rank">Job</TableCell>
        <TableCell sortColumn="name">Shift</TableCell>
        <TableCell>Conflicts</TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
      </TableRow>
      {tableData.map((row, index) => {
        return (
          <TableRow key={index}>
            <TableCell>
              <Column spacing="200">
                <Row spacing={'200'}>
                  <Text type="b300">{row.name}</Text>
                  {row.perfectMatch && <Icon tokens={starTokens} />}
                </Row>

                <Text type="b200" color="secondary">
                  {row.jobType}
                </Text>
              </Column>
            </TableCell>
            <TableCell>
              {!!row.shiftCode && (
                <Column spacing="200">
                  <Text type="b300">{row.shiftCode}</Text>
                  <Text type="b200" color="secondary">
                    {`${row?.jobCapacity || 0} hrs capacity`}
                  </Text>
                </Column>
              )}
            </TableCell>
            <TableCell>
              <Column spacing="200">
                <ConflictTag jobMatch={row} />
                <Text type="b200" color="secondary">
                  {canWorkFullShift(row) ? 'Full Shift' : `Maximum hrs: ${row.maximumHoursAssociateCanDo}`}
                </Text>
              </Column>
            </TableCell>

            <TableCell>
              {!isDisabled(row) ? (
                <Checkbox
                  checked={row.isChecked}
                  onChange={() => onChecked(row.id)}
                  aria-label={`${row.name} Checkbox`}
                />
              ) : (
                <Tooltip
                  title={
                    isJobAtCapacity(row)
                      ? 'Capacity is full.'
                      : 'Apply job modifications to enable selection of this job.'
                  }
                  position="top"
                >
                  <Checkbox disabled={true} aria-label={`${row.name} Checkbox`} />
                </Tooltip>
              )}
            </TableCell>

            <TableCell width={80}>
              <div className="jobMatchesTable__input-wrapper">
                {row.isChecked ? (
                  <Input
                    type="text"
                    value={row.totalHours.toString()}
                    onChange={(totalHours: string) => setTotalHours(row.id, totalHours)}
                    error={!!getTotalHoursErrorMsg(row)}
                    errorMessage={getTotalHoursErrorMsg(row)}
                    size="small"
                    placeholder="hrs..."
                  />
                ) : (
                  ''
                )}
              </div>
            </TableCell>
          </TableRow>
        );
      })}

      <TldAlertModal
        isOpen={isTldModalOpen}
        setIsOpen={setIsTldModalOpen}
        onConfirm={() => {
          toggleJob(tldAlertModal.resultId, jmrResults, dispatch);
        }}
      />
    </Table>
  );
};

export default JobMatchesTable;
